import { WebPubSubClient } from "@azure/web-pubsub-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCreateAssistantThreadMutation } from "../../graphql/generated/graphql";

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
};

export type AssistantDraftState = {
  order: Record<string, unknown> | null;
  missingFields: string[];
};

type UseAssistantRealtimeOptions = {
  open: boolean;
  errorFallback: string;
};

export default function useAssistantRealtime({ open, errorFallback }: UseAssistantRealtimeOptions)
{
  const [threadId, setThreadId] = useState<string | null>(null);
  const [webPubSubUrl, setWebPubSubUrl] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null);
  const [draftState, setDraftState] = useState<AssistantDraftState>({ order: null, missingFields: [] });
  const webPubSubClientRef = useRef<WebPubSubClient | null>(null);
  const creatingThreadRef = useRef(false);
  const isThreadReady = Boolean(threadId && webPubSubUrl);

  const [createAssistantThread] = useCreateAssistantThreadMutation();

  const mergeDraftOrder = useCallback((prev: Record<string, unknown> | null, next: Record<string, unknown> | null): Record<string, unknown> | null =>
  {
    if(!next) return prev ?? null;
    if(!prev) return next;

    const mergeObjects = (left: Record<string, unknown>, right: Record<string, unknown>): Record<string, unknown> =>
    {
      const merged: Record<string, unknown> = { ...left };
      for(const [key, value] of Object.entries(right))
      {
        if(Array.isArray(value))
        {
          merged[key] = value;
          continue;
        }

        if(value && typeof value === 'object' && !Array.isArray(value))
        {
          const leftValue = merged[key];
          if(leftValue && typeof leftValue === 'object' && !Array.isArray(leftValue))
          {
            merged[key] = mergeObjects(leftValue as Record<string, unknown>, value as Record<string, unknown>);
            continue;
          }
        }

        merged[key] = value;
      }
      return merged;
    };

    return mergeObjects(prev, next);
  }, []);

  const parseDraftOrder = useCallback((value: unknown): Record<string, unknown> | null =>
  {
    if(!value) return null;
    if(typeof value === 'string')
    {
      try
      {
        const parsed = JSON.parse(value) as Record<string, unknown>;
        return parsed && typeof parsed === 'object' ? parsed : null;
      }
      catch
      {
        return null;
      }
    }

    return typeof value === 'object' ? value as Record<string, unknown> : null;
  }, []);

  const parseMissingFields = useCallback((value: unknown): string[] =>
  {
    if(!Array.isArray(value)) return [];
    return value.filter(item => typeof item === 'string') as string[];
  }, []);

  const extractStreamingMessage = useCallback((value: string): string | null =>
  {
    if(!value) return null;
    const match = value.match(/"assistantMessage"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)/);
    if(match?.[1])
    {
      return match[1].replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\"/g, '"');
    }

    const trimmed = value.trim();
    if(trimmed.startsWith('{')) return null;
    return value;
  }, []);

  const handleWebPubSubMessage = useCallback((payload: any) =>
  {
    const rawMessage = payload?.message?.data ?? payload?.message ?? payload?.data ?? payload;
    if(!rawMessage || typeof rawMessage !== 'object') return;

    const messageType = rawMessage.type as string | undefined;
    const messageData = rawMessage.data ?? {};

    if(messageType === 'assistant-thinking')
    {
      const partial = typeof messageData.partial === 'string' ? messageData.partial : '';
      const streamingContent = extractStreamingMessage(partial);
      setStreamingMessage(streamingContent);
      return;
    }

    if(messageType === 'assistant-response')
    {
      const content = typeof messageData.message === 'string' ? messageData.message : '';
      const extractedOrder = parseDraftOrder(messageData.extractedData ?? messageData.order ?? null);
      const missingFields = parseMissingFields(messageData.missingFields);

      if(extractedOrder || missingFields.length > 0)
      {
        setDraftState(prev => ({
          order: mergeDraftOrder(prev.order, extractedOrder),
          missingFields
        }));
      }

      if(content)
      {
        setMessages(prev => ([
          ...prev,
          { id: `assistant-${Date.now()}`, role: 'assistant', content, timestamp: new Date().toISOString() }
        ]));
      }
      setStreamingMessage(null);
      return;
    }

    if(messageType === 'error')
    {
      const content = typeof messageData.message === 'string' ? messageData.message : errorFallback;
      setMessages(prev => ([
        ...prev,
        { id: `error-${Date.now()}`, role: 'system', content, timestamp: new Date().toISOString() }
      ]));
      setStreamingMessage(null);
    }
  }, [errorFallback, extractStreamingMessage, mergeDraftOrder, parseDraftOrder, parseMissingFields]);

  const connectWebPubSub = useCallback(async (url: string, thread: string) =>
  {
    const client = new WebPubSubClient(url, { autoReconnect: true });
    webPubSubClientRef.current = client;

    client.on('group-message', handleWebPubSubMessage);

    await client.start();
    await client.joinGroup(`assistant_thread_${thread}`);
  }, [handleWebPubSubMessage]);

  const disconnectWebPubSub = useCallback(async () =>
  {
    const client = webPubSubClientRef.current;
    if(!client) return;

    try
    {
      client.off('group-message', handleWebPubSubMessage);
      await client.stop();
    }
    finally
    {
      webPubSubClientRef.current = null;
    }
  }, [handleWebPubSubMessage]);

  useEffect(() =>
  {
    if(!open)
    {
      setThreadId(null);
      setWebPubSubUrl(null);
      setMessages([]);
      setStreamingMessage(null);
      setDraftState({ order: null, missingFields: [] });
      disconnectWebPubSub();
      return;
    }

    if(creatingThreadRef.current) return;
    creatingThreadRef.current = true;

    (async () =>
    {
      try
      {
        const { data } = await createAssistantThread();
        const thread = data?.CreateAssistantThread?.ThreadId as string | undefined;
        const url = data?.CreateAssistantThread?.WebPubSubUrl as string | undefined;

        if(thread && url)
        {
          setThreadId(thread);
          setWebPubSubUrl(url);
          setMessages([]);
          setStreamingMessage(null);
          setDraftState({ order: null, missingFields: [] });
          await connectWebPubSub(url, thread);
        }
      }
      finally
      {
        creatingThreadRef.current = false;
      }
    })();
  }, [open, createAssistantThread, connectWebPubSub, disconnectWebPubSub]);

  const addMessage = useCallback((message: ChatMessage) =>
  {
    setMessages(prev => ([...prev, message]));
  }, []);

  return {
    threadId,
    isThreadReady,
    messages,
    streamingMessage,
    addMessage,
    draftState,
  };
}
